// RENEW NOW, FOLLOWED TO ITS ADDRESS (the CC's brief, 3 Nov 2026 — his live test: "the green line … seems misleading").
// Renew now only makes the class due (bff renewNow: next_due_at = now(), last_renewed_at untouched); the orchestrator
// issues the address on its next tick. The screen used to say "Due now — next address on the next tick" for as long as
// the page stayed open, the Treasury address made seconds later included. Now the press notes the address in use, the
// renewals are reread every 5 s until the row shows a renewal since — another address in use, or a later lastRenewedAt
// — and then stop; after 3 minutes without one the rereading stops and the screen says so. Both consoles' rows use it.
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { custody as api, type RenewalRow } from './api';

export const RENEW_POLL_MS = 5_000;
export const RENEW_GIVE_UP_MS = 180_000;

/** What the row knew when Renew now was pressed. */
export type RenewFrom = { walletId: number | null; renewedAt: string | null; at: number };

/** Whether the row shows a renewal since the press: another address in use, or a later renewal time. */
export function renewedSince(r: RenewalRow, from: RenewFrom): boolean {
  if (r.active && r.active.walletId !== from.walletId) return true;
  return r.lastRenewedAt != null && r.lastRenewedAt !== from.renewedAt;
}

/** marking: the press is on its way · failed: refused · waiting: marked due, no new address yet · renewed: the row shows
 *  one · late: three minutes and still none (the 30 s read carries on, and turns it to renewed when it comes). */
export type RenewStage = 'idle' | 'marking' | 'failed' | 'waiting' | 'renewed' | 'late';

export function useRenewNow(r: RenewalRow) {
  const qc = useQueryClient();
  const [from, setFrom] = useState<RenewFrom | null>(null);
  const [late, setLate] = useState(false);
  const mutation = useMutation({
    mutationFn: () => api.renewNow(r.class),
    onMutate: () => { setFrom({ walletId: r.active?.walletId ?? null, renewedAt: r.lastRenewedAt, at: Date.now() }); setLate(false); },
    onSuccess: () => { for (const key of ['renewals', 'balances']) void qc.invalidateQueries({ queryKey: ['custody', key] }); },
  });
  const renewed = mutation.isSuccess && from != null && renewedSince(r, from);
  const waiting = mutation.isSuccess && from != null && !renewed && !late;
  useEffect(() => {
    if (!waiting || !from) return;
    const poll = setInterval(() => { void qc.invalidateQueries({ queryKey: ['custody', 'renewals'] }); }, RENEW_POLL_MS);
    const stop = setTimeout(() => setLate(true), Math.max(0, from.at + RENEW_GIVE_UP_MS - Date.now()));
    return () => { clearInterval(poll); clearTimeout(stop); };
  }, [waiting, from, qc]);
  // The new address is a new wallet row: the balances learn of it once, when it shows.
  useEffect(() => { if (renewed) void qc.invalidateQueries({ queryKey: ['custody', 'balances'] }); }, [renewed, qc]);
  const stage: RenewStage = mutation.isPending ? 'marking' : mutation.isError ? 'failed'
    : renewed ? 'renewed' : !mutation.isSuccess ? 'idle' : late ? 'late' : 'waiting';
  return { stage, error: mutation.error, renew: () => mutation.mutate() };
}
